/**
 * Converts a video URL (YouTube, Vimeo) to an embeddable iframe src.
 * Returns null if the URL is not a recognized embeddable format,
 * in which case callers should fall back to a native video player.
 */
export function toEmbedUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  const ytId = extractYouTubeVideoId(trimmed);
  if (ytId) {
    return `https://www.youtube-nocookie.com/embed/${ytId}`;
  }

  if (trimmed.includes("player.vimeo.com/video/")) {
    return trimmed;
  }
  const vimeoId = extractVimeoVideoId(trimmed);
  if (vimeoId) {
    return `https://player.vimeo.com/video/${vimeoId}`;
  }

  return null;
}

// Video ID extraction

export function extractYouTubeVideoId(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }
  const watchMatch = trimmed.match(/(?:youtube\.com\/watch\?.*v=)([\w-]+)/);
  if (watchMatch) {
    return watchMatch[1];
  }
  const shortMatch = trimmed.match(/youtu\.be\/([\w-]+)/);
  if (shortMatch) {
    return shortMatch[1];
  }
  const embedMatch = trimmed.match(
    /youtube(?:-nocookie)?\.com\/embed\/([\w-]+)/,
  );
  if (embedMatch) {
    return embedMatch[1];
  }
  return null;
}

/**
 * Extracts the video ID from various Vimeo URL formats:
 *  - vimeo.com/304924537
 *  - vimeo.com/channels/staffpicks/304924537
 *  - vimeo.com/groups/name/videos/304924537
 *  - vimeo.com/showcase/5341657/video/304924537
 *  - vimeo.com/showcase/5341657?video=304924537
 *  - vimeo.com/album/5341657/video/304924537
 */
export function extractVimeoVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("vimeo.com")) {
      return null;
    }

    const queryVideoId = parsed.searchParams.get("video");
    if (queryVideoId && /^\d+$/.test(queryVideoId)) {
      return queryVideoId;
    }

    const videoPathMatch = parsed.pathname.match(/\/videos?\/(\d+)/);
    if (videoPathMatch) {
      return videoPathMatch[1];
    }

    const trailingIdMatch = parsed.pathname.match(/\/(\d+)(?:\/)?$/);
    if (trailingIdMatch) {
      return trailingIdMatch[1];
    }

    return null;
  } catch {
    return null;
  }
}

// Duration detection — Vimeo oEmbed API
export async function fetchVimeoDurationInMinutes(
  url: string,
): Promise<number | null> {
  const videoId = extractVimeoVideoId(url);
  if (!videoId) {
    return null;
  }
  try {
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
      `https://vimeo.com/${videoId}`,
    )}`;
    const res = await fetch(oembedUrl, {
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    return typeof data.duration === "number" && data.duration > 0
      ? Math.ceil(data.duration / 60)
      : null;
  } catch {
    return null;
  }
}

// YouTube IFrame API types

interface YTPlayer {
  getDuration(): number;
  destroy(): void;
}

interface YTPlayerEvent {
  target: YTPlayer;
}

interface YTPlayerConstructor {
  new (
    element: HTMLElement,
    options: {
      videoId: string;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: (event: YTPlayerEvent) => void;
      };
    },
  ): YTPlayer;
}

interface YTNamespace {
  Player: YTPlayerConstructor;
  loaded?: number;
}

interface WindowWithYT extends Window {
  YT?: YTNamespace;
  onYouTubeIframeAPIReady?: (() => void) | null;
}

function getYTWindow(): WindowWithYT {
  return window as unknown as WindowWithYT;
}

// Duration detection — YouTube IFrame API

let ytApiLoadPromise: Promise<void> | null = null;

function loadYouTubeIframeApi(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("No window"));
  }
  const ytWindow = getYTWindow();
  if (ytWindow.YT?.loaded) {
    return Promise.resolve();
  }
  if (ytApiLoadPromise) {
    return ytApiLoadPromise;
  }
  ytApiLoadPromise = new Promise<void>(resolve => {
    const prev = ytWindow.onYouTubeIframeAPIReady;
    ytWindow.onYouTubeIframeAPIReady = () => {
      if (prev) {
        prev();
      }
      resolve();
    };
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    document.head.appendChild(script);
  });
  return ytApiLoadPromise;
}

// YT.Player's onReady fires before video metadata is fully loaded,
// so getDuration() may initially return 0. We poll until it yields
// a positive value, with a hard timeout as a safety net.
const YT_DURATION_TIMEOUT_MS = 15_000;
const YT_DURATION_POLL_INTERVAL_MS = 500;
const YT_DURATION_MAX_POLLS = 20;

export async function fetchYouTubeDurationInMinutes(
  videoId: string,
): Promise<number | null> {
  try {
    await loadYouTubeIframeApi();

    const ytWindow = getYTWindow();
    if (!ytWindow.YT) {
      return null;
    }

    const container = document.createElement("div");
    container.style.cssText =
      "position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden";
    document.body.appendChild(container);

    return new Promise<number | null>(resolve => {
      let settled = false;
      let player: YTPlayer | null = null;

      const cleanup = () => {
        if (settled) {
          return;
        }
        settled = true;
        try {
          player?.destroy();
        } catch {
          /* noop */
        }
        container.remove();
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, YT_DURATION_TIMEOUT_MS);

      player = new ytWindow.YT!.Player(container, {
        videoId,
        playerVars: { autoplay: 0, controls: 0 },
        events: {
          onReady: (event: YTPlayerEvent) => {
            let polls = 0;
            const poll = () => {
              if (settled) {
                return;
              }
              const duration = event.target.getDuration();
              if (duration > 0) {
                clearTimeout(timeout);
                cleanup();
                resolve(Math.ceil(duration / 60));
              } else if (polls < YT_DURATION_MAX_POLLS) {
                polls++;
                setTimeout(poll, YT_DURATION_POLL_INTERVAL_MS);
              } else {
                clearTimeout(timeout);
                cleanup();
                resolve(null);
              }
            };
            poll();
          },
        },
      });
    });
  } catch {
    return null;
  }
}

// Unified entry point

export async function fetchExternalVideoDurationInMinutes(
  url: string,
): Promise<number | null> {
  const ytId = extractYouTubeVideoId(url);
  if (ytId) {
    return fetchYouTubeDurationInMinutes(ytId);
  }
  return fetchVimeoDurationInMinutes(url);
}

// Duration detection — uploaded video files (HTML5 Video element)

// Guard against slow or unreachable URLs where the browser never
// fires loadedmetadata (e.g. CORS-blocked or non-video resources).
const VIDEO_METADATA_TIMEOUT_MS = 10_000;

/**
 * Probes the duration of a directly-accessible video file URL
 * by loading its metadata into a temporary <video> element.
 */
export function fetchUploadedVideoDurationInMinutes(
  url: string,
): Promise<number | null> {
  return new Promise(resolve => {
    const video = document.createElement("video");
    video.preload = "metadata";

    const timeout = setTimeout(() => {
      video.src = "";
      resolve(null);
    }, VIDEO_METADATA_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      const minutes =
        video.duration > 0 ? Math.ceil(video.duration / 60) : null;
      video.src = "";
      resolve(minutes);
    };

    video.onerror = () => {
      clearTimeout(timeout);
      video.src = "";
      resolve(null);
    };

    video.src = url;
  });
}
